import { useState } from 'react';
import { httpClient } from '../api/http';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Alert } from '@piggytechng/component-library';
import { authPayload } from '../helpers/utils/auth-payload';
import useRolePermissions from './useRolePermissions';
import { formatLegacyAllTargetRow, formatLegacyTargetRow, formatAllTargetItem } from '../helpers/utils/targetSavings';
import { TargetListItem, TargetListPage, TargetListQuery, AdjustableTarget, TargetAdjustPayload } from '../types';

// Hard cap on a single manual Target credit / debit (core enforces the same)
export const TARGET_ADJUST_MAX_AMOUNT = 1000000;

const LEGACY_TARGET_LIST_ENDPOINT: Record<TargetListQuery['status'], string> = {
  active: 'pending',
  completed: 'completed',
  all: 'all-targets',
};

const toPositiveInt = (value: unknown, fallback: number) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback;
};

// Fallback for when GET admin/target-savings/list/:userId is not deployed yet: reuse the
// legacy pending/completed/all-targets calls with the requested page/limit and filter the
// loaded page client-side by title.
const fetchTargetListFallback = async (
  userId: string,
  { status, q = '', page = 1, limit = 20 }: TargetListQuery,
): Promise<TargetListPage> => {
  const url = `admin/target-savings/${LEGACY_TARGET_LIST_ENDPOINT[status]}/${userId}/${page}/${limit}`;
  const response = await httpClient.get(url);
  const data = response?.data?.data ?? {};

  const rows: TargetListItem[] =
    status === 'all'
      ? (data?.results ?? []).map(formatLegacyAllTargetRow)
      : (data?.list ?? []).map((item: any) => formatLegacyTargetRow(item, status));

  const needle = q.trim().toLowerCase();
  const results = needle ? rows.filter((row) => (row.title ?? '').toLowerCase().includes(needle)) : rows;

  const total = toPositiveInt(data?.total, rows.length);
  const totalPages = toPositiveInt(
    data?.lastPage ?? data?.totalPages,
    Math.max(1, Math.ceil(total / limit), rows.length >= limit ? page + 1 : page),
  );

  return { results, total, page, limit, totalPages, fallback: true };
};

// GET admin/target-savings/list/:userId?status&q&page&limit (core). A 404 means the endpoint
// has not shipped yet, so we fall back to the legacy calls; other errors are surfaced once.
export const fetchTargetList = async (userId: string, query: TargetListQuery): Promise<TargetListPage> => {
  const { status, q = '', page = 1, limit = 20 } = query;
  const trimmed = q.trim();

  try {
    const response = await httpClient.get(`admin/target-savings/list/${userId}`, {
      params: { status, q: trimmed || undefined, page, limit },
      hideError: true,
    });
    const data = response?.data?.data ?? {};
    const results: TargetListItem[] = Array.isArray(data?.results) ? data.results : [];
    const resolvedLimit = toPositiveInt(data?.limit, limit);
    const total = toPositiveInt(data?.total, results.length);

    return {
      results,
      total,
      page: toPositiveInt(data?.page, page),
      limit: resolvedLimit,
      totalPages: toPositiveInt(data?.totalPages, Math.max(1, Math.ceil(total / resolvedLimit))),
      fallback: false,
    };
  } catch (err: any) {
    if (err?.response?.status === 404) {
      return fetchTargetListFallback(userId, { status, q: trimmed, page, limit });
    }
    // hideError suppressed the interceptor toast, so surface the backend message once here.
    Alert.error(err?.response?.data?.message || 'Could not load targets');
    throw err;
  }
};

const useTargetSavings = () => {
  const { isSuperAdmin } = useRolePermissions({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fetchingSettings, setFetchingSettings] = useState<boolean>(false);
  const [fetchingTransactions, setFetchingTransactions] = useState<boolean>(false);
  const [isRunningTargetAction, setIsRunningTargetAction] = useState<boolean>(false);
  const [isAdjustingTarget, setIsAdjustingTarget] = useState<boolean>(false);
  const [isFetchingAdjustableTargets, setIsFetchingAdjustableTargets] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const params = useParams();
  const userIdIn = params.id as string;

  const [targetSavingsDetails, setTargetSavingsDetails] = useState<{
    walletBalance: number;
    totalWallets: number;
  }>();

  const [completedTargetSavings, setCompletedTargetSavings] = useState<{
    currentPage: number;
    lastPage: number;
    list: any;
    nextPage: number;
    total: number;
  }>();

  const [pendingTargetSavings, setPendingTargetSavings] = useState<{
    currentPage: number;
    lastPage: number;
    list: any;
    nextPage: number;
    total: number;
  }>();

  const [paginationData, setPaginationData] = useState({
    currentPage: 0,
    nextPage: 1,
    lastPage: 1,
    perPage: 10,
    total: 0,
  });

  const [targetSavingsTransactions, setTargetSavingsTransactions] = useState<any>();

  const [targetSavingsSettings, setTargetSavingsSettings] = useState<{
    name: string;
    startDate: number;
    endDate: number;
    frequency: string;
    amount: number;
    balance?: number;
    targetAmount: number;
    interestData: {
      accruedamount: number;
      accruedpercent: number;
      accruedcounter: number;
    };
    nextInterestDate: number;
    activities: {
      results: [any];
      total: number;
    };
    isLocked: boolean;
    targetStatus: 0 | 1;
    userId: string;
    lastTransactionDate: number;
  }>();

  const [transactionsQueryType, setTransactionsQueryType] = useState<'fetch-all' | 'query'>();

  const [searchForm, setSearchForm] = useState({
    query: '',
  });

  const handleChange = (e: { target: { name: string; value: string } }) => {
    const { value } = e.target;
    setSearchForm({ query: value });
  };

  const runTargetAction = async (targetId: string, payload: any, callback: (resp?: any) => void) => {
    // format dates to unix timestamp
    if (payload.startDate) {
      payload.startDate = Math.floor(new Date(payload.startDate).getTime() / 1000);
    }
    if (payload.stopDate) {
      payload.stopDate = Math.floor(new Date(payload.stopDate).getTime() / 1000);
    }

    try {
      setIsRunningTargetAction(true);
      const response = await httpClient.post(`/admin/target-savings/quick-actions/${targetId}`, {
        ...payload,
        ...authPayload(payload.password),
      });
      if (callback) {
        callback(response);
      }
      Alert.success(response?.data?.message || 'Action performed successfully');
      return Promise.resolve(response);
    } catch (err: any) {
      console.log(err?.response?.data?.message || 'An error occured trying to perform the action');
    } finally {
      setIsRunningTargetAction(false);
    }
  };

  const [targetUpdateForm, setTargetUpdateForm] = useState({
    startDate: '',
    endDate: '',
    frequency: '',
    periodicAmount: '',
  });

  const {
    data: pendingTargetSavingsQ,
    // isLoading: isLoadingPendingTargetSavingsQ,
    // refetch: refetchPendingTargetSavingsQ,
  } = useQuery({
    queryKey: ['pending-target-savings-details' + userIdIn],
    queryFn: () => httpClient.get(`admin/target-savings/pending/${userIdIn}/${1}/${10}`).then((res) => res.data.data),
  });

  const {
    data: completedTargetSavingsQ,
    // isLoading: isLoadingCompletedTargetSavings,
    // refetch: refetchCompletedTargetSavings,
  } = useQuery({
    queryKey: ['completed-target-savings-details' + userIdIn],
    queryFn: () => httpClient.get(`admin/target-savings/completed/${userIdIn}/${1}/${10}`).then((res) => res.data.data),
  });

  const { data: allTargetSavingsQ } = useQuery({
    queryKey: ['all-target-savings' + userIdIn],
    queryFn: () =>
      httpClient.get(`admin/target-savings/all-targets/${userIdIn}/${1}/${10}`).then((res) => {
        const data = res.data.data;
        return {
          ...data,
          list: (data?.results ?? []).map(formatAllTargetItem),
        };
      }),
  });

  const {
    data: targetSavingsDetailsQ,
    // isLoading: isLoadingtargetSavingsDetails,
    // refetch: refetchHouseMoneyDetails,
  } = useQuery({
    queryKey: ['target-savings-details' + userIdIn],
    queryFn: () => httpClient.get(`admin/target-savings/details/${userIdIn}`).then((res) => res.data.data),
  });

  const fetchTargetSavingsDetails = async (userId: string) => {
    try {
      setIsLoading(true);
      const url = `admin/target-savings/details/${userId}`;
      const response = await httpClient.get(url);
      if (response?.data?.status) {
        setTargetSavingsDetails(response?.data?.data);
      }
    } catch (error) {
      console.log(error);
      return error;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTargetSavingsTransactions = async (userId: string, targetId: string, page: number, limit: number) => {
    try {
      setFetchingTransactions(true);
      const url = `admin/target-savings/transactions/${userId}/${targetId}/${page}/${limit}`;
      const response = await httpClient.get(url);
      if (response?.data?.status) {
        const { data } = response.data;
        setTargetSavingsTransactions(response?.data?.data?.list);
        setPaginationData({
          currentPage: data.currentPage + 1,
          nextPage: data.nextPage,
          lastPage: data.lastPage,
          perPage: data.perPage,
          total: data.total,
        });
      }
      setTransactionsQueryType('fetch-all');
    } catch (error) {
      console.log(error);
      return error;
    } finally {
      setFetchingTransactions(false);
    }
  };

  const searchTargetSavingsTransactions = async (
    userId: string,
    targetId: string,
    query: string,
    page: number,
    limit: number,
  ) => {
    try {
      setFetchingTransactions(true);
      const url = `admin/target-savings/search/transactions?userId=${userId}&targetId=${targetId}&query=${query}&page=${page}&limit=${limit}`;
      const response = await httpClient.get(url);
      if (response?.data?.status) {
        const { data } = response.data;
        setTargetSavingsTransactions(response?.data?.data?.list);
        setPaginationData({
          currentPage: data.currentPage + 1,
          nextPage: data.nextPage,
          lastPage: data.lastPage,
          perPage: data.perPage,
          total: data.total,
        });
      }
      setTransactionsQueryType('query');
    } catch (error) {
      console.log(error);
    } finally {
      setFetchingTransactions(false);
    }
  };

  const fetchTargetSavingsSettings = async (userId: string, targetId: string) => {
    try {
      setFetchingSettings(true);
      const url = `admin/target-savings/settings/${userId}/${targetId}`;
      const response = await httpClient.get(url);
      if (response?.data?.status) {
        setTargetSavingsSettings(response?.data?.data);
      }
    } catch (error) {
      console.log(error);
      return error;
    } finally {
      setFetchingSettings(false);
    }
  };

  const fetchCompletedTargetSavings = async (userId: string, page: number, limit: number) => {
    try {
      setIsLoading(true);
      const url = `admin/target-savings/completed/${userId}/${page}/${limit}`;
      const response = await httpClient.get(url);
      if (response?.data?.status) {
        setCompletedTargetSavings(response?.data?.data);
      }
    } catch (error) {
      console.log(error);
      return error;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPendingTargetSavings = async (userId: string, page: number, limit: number) => {
    try {
      setIsLoading(true);
      const url = `admin/target-savings/pending/${userId}/${page}/${limit}`;
      const response = await httpClient.get(url);
      if (response?.data?.status) {
        setPendingTargetSavings(response?.data?.data);
      }
    } catch (error) {
      console.log(error);
      return error;
    } finally {
      setIsLoading(false);
    }
  };

  // Active targets a manual credit / debit can be posted to. Prefers the new list route and falls back to the
  // older pending route (same page/limit shape) while core is still rolling the list route out.
  const fetchAdjustableTargets = async (userId: string): Promise<AdjustableTarget[]> => {
    const normalize = (items: any[]): AdjustableTarget[] =>
      (items ?? [])
        .filter((item) => item?.targetId)
        .map((item) => ({
          targetId: String(item.targetId),
          title: item.title ?? item.name ?? item.targetId,
          userBalance: item.userBalance ?? item.balance ?? item.keyPoints?.[1]?.value ?? null,
        }));

    try {
      setIsFetchingAdjustableTargets(true);
      try {
        const response = await httpClient.get(`admin/target-savings/list/${userId}?status=active&page=1&limit=100`, {
          hideError: true,
        });
        if (response?.data?.status) {
          const data = response.data.data;
          return normalize(data?.results ?? data?.list ?? []);
        }
      } catch (error: any) {
        if (error?.response?.status !== 404) throw error;
      }
      const fallback = await httpClient.get(`admin/target-savings/pending/${userId}/1/100`, { hideError: true });
      const data = fallback?.data?.data;
      return normalize(data?.list ?? data?.results ?? []);
    } catch (error: any) {
      console.log(error);
      Alert.error(error?.response?.data?.message || 'Failed to fetch active targets');
      return [];
    } finally {
      setIsFetchingAdjustableTargets(false);
    }
  };

  const adjustTargetBalance = async (
    userId: string,
    targetId: string,
    payload: TargetAdjustPayload,
    password: string,
    callback?: () => void,
  ) => {
    if (!isSuperAdmin) {
      Alert.warn('You do not have access to perform this action.');
      return;
    }
    if (!payload.amount || payload.amount <= 0) {
      Alert.error('Enter an amount greater than zero');
      return;
    }
    if (payload.amount > TARGET_ADJUST_MAX_AMOUNT) {
      Alert.error(`A single target adjustment cannot exceed ${TARGET_ADJUST_MAX_AMOUNT.toLocaleString()}`);
      return;
    }
    try {
      setIsAdjustingTarget(true);
      const response = await httpClient.post<any>(`admin/target-savings/adjust/${userId}/${targetId}`, {
        ...payload,
        ...authPayload(password),
      });
      if (response.data.status) {
        Alert.success(
          response.data.message || `Target ${payload.type === 'credit' ? 'credited' : 'debited'} successfully`,
        );
        callback?.();
        queryClient.invalidateQueries({ queryKey: ['pending-target-savings-details' + userId] });
        queryClient.invalidateQueries({ queryKey: ['target-savings-details' + userId] });
        queryClient.invalidateQueries({ queryKey: ['user-balances' + userId] });
      }
    } catch (err: any) {
      console.log(err?.response?.data?.message || 'An error occured trying to adjust the target');
    } finally {
      setIsAdjustingTarget(false);
    }
  };

  return {
    fetchAdjustableTargets,
    isFetchingAdjustableTargets,
    adjustTargetBalance,
    isAdjustingTarget,
    targetSavingsDetails,
    fetchTargetSavingsDetails,
    fetchPendingTargetSavings,
    pendingTargetSavings,
    fetchCompletedTargetSavings,
    completedTargetSavings,
    targetUpdateForm,
    setTargetUpdateForm,
    fetchTargetSavingsTransactions,
    targetSavingsTransactions,
    fetchTargetSavingsSettings,
    targetSavingsSettings,
    searchTargetSavingsTransactions,
    searchForm,
    handleChange,
    transactionsQueryType,
    paginationData,
    fetchingSettings,
    setFetchingSettings,
    fetchingTransactions,
    setFetchingTransactions,
    isLoading,
    targetSavingsDetailsQ,
    pendingTargetSavingsQ,
    completedTargetSavingsQ,
    allTargetSavingsQ,
    fetchTargetList,
    runTargetAction,
    isRunningTargetAction,
  };
};

export default useTargetSavings;
